"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Download, ExternalLink, Maximize2, Copy, Check } from "lucide-react";
import { cn } from "../lib/utils";

interface ImageConfig {
  title?: string;
  description?: string;
  alt?: string;
  width?: number;
  height?: number;
  caption?: string;
  source?: string;
  type?: 'diagram' | 'chart' | 'mermaid' | 'flowchart' | 'screenshot' | 'photo' | 'illustration' | 'other';
}

interface ImageData {
  imageUrl: string;
  config: ImageConfig;
}

interface AIImageRendererProps {
  imageData: ImageData;
  className?: string;
}

const AIImageRenderer: React.FC<AIImageRendererProps> = ({ imageData, className }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { imageUrl, config } = imageData;
  const {
    title = "Generated Image",
    description,
    alt = "Generated image",
    caption,
    source,
    type = "other"
  } = config;

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleDownload = async () => {
    try {
      if (imageUrl.startsWith('data:')) {
        // Handle base64 data URLs
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Handle regular URLs
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'mermaid':
      case 'diagram':
      case 'flowchart':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'chart':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'screenshot':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'photo':
      case 'illustration':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={cn("w-full", className)}
      >
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-lg font-semibold">{title}</CardTitle>
                {description && (
                  <CardDescription className="text-sm text-muted-foreground">
                    {description}
                  </CardDescription>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className={getTypeColor(type)}>
                  {type}
                </Badge>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyUrl}
                    className="h-8 w-8 p-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownload}
                    className="h-8 w-8 p-0"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsFullscreen(true)}
                    className="h-8 w-8 p-0"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                  {source && (
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      className="h-8 w-8 p-0"
                    >
                      <a href={source} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative bg-muted/30">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-current animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 rounded-full bg-current animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 rounded-full bg-current animate-bounce"></div>
                  </div>
                </div>
              )}
              {hasError ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <p className="text-sm">Failed to load image</p>
                    <p className="text-xs mt-1">Please check the image URL</p>
                  </div>
                </div>
              ) : (
                <img
                  src={imageUrl}
                  alt={alt}
                  className="w-full h-auto max-h-[600px] object-contain"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    display: isLoading ? 'none' : 'block'
                  }}
                />
              )}
            </div>
            {caption && (
              <div className="p-4 border-t bg-muted/20">
                <p className="text-sm text-muted-foreground">{caption}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
            >
              ✕ Close
            </Button>
            <img
              src={imageUrl}
              alt={alt}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AIImageRenderer; 